import React, { useState } from "react";
import { Modal } from "antd";
import { MdErrorOutline, MdDownload } from "react-icons/md";
import { turnoApi } from "../../services/api/turnoApi";
import * as XLSX from "xlsx";

const ModalErroresCargaMasivaTurno = ({
  turnoSeleccionado,
  isOpen,
  onClose,
  showToast,
}) => {
  const [errores, setErrores] = useState([]);
  const [loading, setLoading] = useState(false);

  const exportarErroresExcel = () => {
    if (!errores.length) {
      showToast?.("info", "No hay errores para exportar");
      return;
    }

    const turnoLabel = turnoSeleccionado
      ? [
          turnoSeleccionado.comedor?.nombre,
          turnoSeleccionado.tipo_servicio_alimentacion?.descripcion,
          turnoSeleccionado.fecha_turno?.split("T")[0],
        ]
          .filter(Boolean)
          .join(" / ")
      : "Turno";

    const filasDetalle = errores.map((err) => ({
      "Fila Excel": err.fila_excel ?? "",
      Documento: err.numero_documento || "",
      Errores: Array.isArray(err.errores) ? err.errores.join(" | ") : "",
      "Fecha registro": err.created_at
        ? new Date(err.created_at).toLocaleString("es-PE")
        : "",
      Regularizado: err.regularizado ? "Sí" : "No",
    }));

    const filasResumen = [
      { Campo: "Turno", Valor: turnoLabel },
      { Campo: "Total errores pendientes", Valor: errores.length },
      {
        Campo: "Generado el",
        Valor: new Date().toLocaleString("es-PE", { timeZone: "America/Lima" }),
      },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasResumen), "Resumen");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasDetalle), "Errores");
    XLSX.writeFile(
      wb,
      `Errores_Carga_Masiva_Turno_${turnoSeleccionado?.id || "turno"}_${Date.now()}.xlsx`
    );
    showToast?.("success", `Excel exportado con ${errores.length} error(es)`);
  };

  React.useEffect(() => {
    if (!isOpen || !turnoSeleccionado?.id) return;

    const cargar = async () => {
      setLoading(true);
      try {
        const response = await turnoApi.getErroresCargaMasiva(turnoSeleccionado.id);
        setErrores(response.data?.data || []);
      } catch (error) {
        console.error("Error al cargar errores:", error);
        showToast?.("error", "No se pudieron cargar los errores del turno");
        setErrores([]);
      } finally {
        setLoading(false);
      }
    };

    cargar();
  }, [isOpen, turnoSeleccionado?.id, showToast]);

  return (
    <>
      <style>
        {`
          .modal-errores-carga .ant-modal-content {
            border-radius: 20px;
            overflow: hidden;
          }
          .modal-errores-carga .ant-modal-header {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            border-bottom: none;
            padding: 1.25rem 1.5rem;
          }
          .modal-errores-carga .ant-modal-title {
            color: white;
            font-weight: 700;
          }
          .modal-errores-carga .ant-modal-close {
            color: white;
          }
        `}
      </style>
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <MdErrorOutline size={22} />
            Errores de carga masiva
          </div>
        }
        open={isOpen}
        onCancel={onClose}
        footer={null}
        width={900}
        className="modal-errores-carga"
        destroyOnClose
      >
        {turnoSeleccionado && (
          <div
            className="dh-alert dh-alert-info"
            style={{
              background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
              border: "2px solid #3b82f6",
              borderRadius: 12,
              padding: "0.75rem 1rem",
              color: "#1e40af",
              marginBottom: "1rem",
              fontSize: "0.9rem",
            }}
          >
            <strong>Turno:</strong> {turnoSeleccionado.comedor?.nombre || "N/A"} —{" "}
            {turnoSeleccionado.tipo_servicio_alimentacion?.descripcion || "N/A"} —{" "}
            {turnoSeleccionado.fecha_turno
              ? (() => {
                  const [y, m, d] = turnoSeleccionado.fecha_turno.split("T")[0].split("-");
                  return `${d}/${m}/${y}`;
                })()
              : "N/A"}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <span className="spinner-border spinner-border-sm me-2" />
            Cargando errores...
          </div>
        ) : errores.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}>
            No hay errores pendientes de regularización para este turno.
          </div>
        ) : (
          <>
            <div style={{ marginBottom: "1rem", display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn-dh-primary"
                onClick={exportarErroresExcel}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  borderRadius: 12,
                }}
              >
                <MdDownload size={18} />
                Exportar errores Excel
              </button>
            </div>
            <div style={{ maxHeight: 420, overflowY: "auto" }}>
            <table className="table table-sm table-hover" style={{ fontSize: "0.85rem" }}>
              <thead>
                <tr>
                  <th>Fila Excel</th>
                  <th>Documento</th>
                  <th>Errores</th>
                  <th>Fecha registro</th>
                </tr>
              </thead>
              <tbody>
                {errores.map((err) => (
                  <tr key={err.id}>
                    <td>{err.fila_excel}</td>
                    <td style={{ fontFamily: "monospace" }}>{err.numero_documento || "—"}</td>
                    <td>
                      <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                        {(err.errores || []).map((msg, i) => (
                          <li key={i}>{msg}</li>
                        ))}
                      </ul>
                    </td>
                    <td>
                      {err.created_at
                        ? new Date(err.created_at).toLocaleString("es-PE")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default ModalErroresCargaMasivaTurno;
